import type { AbstractControl } from '@angular/forms';
import {
  CapturedHereRow, MapFeatureLink, MapFeatureLinkCapture, MapFeatureLinkCaptureRegion, MapFeatureLinkPayload, MapFeatureLinkPin,
  MapFeatureLinkSheet, MapFeatureLinkSheetTarget,
} from '../models/map-feature-link';
import type { DrawingRevisionDetail, DrawingSheet } from '../models/drawing-sheet';
import type { MarkupSaveResult } from '../components/markup-editor/markup-editor.types';
import type { InspectionQuery, MapFeatureCandidate } from './project-map-inspection';
import type { Markup } from '../models/markup';

/**
 * Design context = one `map_feature_link` on a pin-bearing record. These helpers are the
 * single place that knows how the picker result, the form control value and the api
 * payload relate, so every editor wires the link the same way.
 */

/** The keys a location picker or station-range dialog may send back. */
export interface PickedLocationResult {
  latitude?: number | string | null;
  longitude?: number | string | null;
  location?: string | null;
  locationText?: string | null;
  station_range?: unknown;
  map_feature_link?: MapFeatureLink | null;
}

export interface PickedLocationPatch {
  latitude: number | string | null;
  longitude: number | string | null;
  location: string | null;
  station_range: unknown;
  map_feature_link: MapFeatureLink | null;
}

interface LinkCarrier { map_feature_link?: unknown }
interface LinkMarker extends LinkCarrier { data?: LinkCarrier & { allLineItems?: (LinkCarrier | null)[] } }

/**
 * Form value → api key (R54: the `sheet` key goes only with a drawing-location edit made in this session).
 * - undefined leaves the link untouched.
 * - null is a bare null, unless `removed` (an explicit Remove / Clear this session) makes it `{ sheet: null }`. The api
 *   treats a bare null as nothing on a record without a link and keeps a sheet target someone else added meanwhile
 *   (R24: it drops only the feature part), so a null the user never touched can't delete a drawing location.
 * - A feature link sends its reference and pin, and its sheet target only when picked in this session
 *   (`sheet_edited`, amendment 2: the reference and pin go with it). A stored target is not echoed: an absent `sheet`
 *   keeps the link's own.
 * - A stored sheet-only link the user did not change is left untouched (undefined).
 * - An already-converted payload (a second conversion) keeps what the first one decided, including `{ sheet: null }`.
 */
export function mapFeatureLinkPayload(value: MapFeatureLink | null | undefined, removed = false): MapFeatureLinkPayload | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return removed ? { sheet: null } : null;
  const sheet = sheetTarget(value.sheet);
  // Neither a feature nor a readable sheet: the removal payload `{ sheet: null }` (no `uuid` key) stays one; a stored
  // link in that state (its revision row gone) is left untouched rather than deleted
  if (!value.map_feature_ref && !sheet) return 'uuid' in value ? undefined : { sheet: null };
  const sendSheet = !!sheet && sheetEdited(value);
  if (!value.map_feature_ref && !sendSheet) return undefined;
  const payload: MapFeatureLinkPayload = {};
  if (value.map_feature_ref) {
    const pin: MapFeatureLinkPin = { latitude: Number(value.pin.latitude), longitude: Number(value.pin.longitude) };
    if (typeof value.pin.accuracy_m === 'number' && Number.isFinite(value.pin.accuracy_m)) pin.accuracy_m = value.pin.accuracy_m;
    // Explicit keys, not a spread: send only the contract's five reference keys, never UI-side fields
    // (the api ignores unknown keys rather than rejecting them).
    const { catalogRevision, layerId, sourceVersionId, sublayerKey, featureId } = value.map_feature_ref;
    payload.map_feature_ref = { catalogRevision, layerId, sourceVersionId, sublayerKey, featureId };
    payload.pin = pin;
  }
  // A sheet-only link never echoes a pin: the api derives it from a placement.
  if (sendSheet) payload.sheet = sheet;
  // Its drawing pin removed this session: an explicit null drops the stored sheet target. An untouched stored
  // feature-only link (serialized `sheet: null`) omits the key, so it never deletes a target added elsewhere.
  else if (value.map_feature_ref && value.sheet === null && sheetEdited(value)) payload.sheet = null;
  payload.snapshot = true;
  if (typeof value.client_key === 'string' && value.client_key) payload.client_key = value.client_key;
  // The capture is tri-state (spec 2026-09-27 §3.2): the key travels only when the link carries it.
  // Idempotent: an editor may convert first and the whole-report save converts again, so a
  // capture that is already the uuid string passes through (reading `.attachment_uuid` off it drops the key).
  if (value.capture !== undefined) payload.capture = captureUuid(value.capture as MapFeatureLinkCapture | string | null);
  // Its map region (spec addendum §11) follows it: null with a cleared capture, the capture's region when known, and
  // on a second conversion (capture already its uuid) the region the first one sent. Otherwise the key is omitted.
  if (value.capture === null) payload.capture_region = null;
  else if (value.capture !== undefined) {
    const passThrough = typeof value.capture === 'string';
    if (passThrough && value.capture_region === null) payload.capture_region = null;
    else {
      const region = cleanCaptureRegion(passThrough ? value.capture_region : value.capture.region);
      if (region) payload.capture_region = region;
    }
  }
  return payload;
}

/**
 * The sheet target was set in this session: picked (`sheet_edited`), on an unsaved draft (`uuid: null`, e.g. the
 * Drawings viewer's Add to), or already decided by a first conversion (a payload, whose `snapshot` is a boolean).
 */
function sheetEdited(value: MapFeatureLink): boolean {
  return value.sheet_edited === true || value.uuid === null || typeof (value as { snapshot?: unknown }).snapshot === 'boolean';
}

/**
 * The serialized (or already converted) sheet block → the write shape `{ revision_uuid, point }`; null when absent. A
 * markup-only location (no pin) sends `point: null` explicitly: the api reads a missing key as malformed.
 */
function sheetTarget(sheet: MapFeatureLinkSheet | MapFeatureLinkSheetTarget | null | undefined): MapFeatureLinkSheetTarget | null {
  if (!sheet || typeof sheet !== 'object' || !sheet.revision_uuid) return null;
  if (!sheet.point) return { revision_uuid: sheet.revision_uuid, point: null };
  const { page, x, y } = sheet.point;
  return { revision_uuid: sheet.revision_uuid, point: { page: Number(page), x: Number(x), y: Number(y) } };
}

const REGION_BOUNDS = ['north', 'south', 'east', 'west', 'width_px', 'height_px'] as const;
const REGION_CAMERA = ['zoom', 'bearing'] as const;

function regionNumber(raw: unknown): number | null {
  const value = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A capture's map region in the api's `CaptureRegionSchema` shape (api 4bb62466): the bounds and pixel size are
 * required finite numbers (numeric strings are coerced), latitudes within ±90, longitudes within ±180, the pixel
 * size positive integers; `zoom` / `bearing` may be unknown (null, like mobile). Anything else → null, so the payload
 * omits it rather than have the api log and drop it.
 */
export function cleanCaptureRegion(region: unknown): MapFeatureLinkCaptureRegion | null {
  if (!region || typeof region !== 'object' || Array.isArray(region)) return null;
  const source = region as Record<string, unknown>;
  const out = {} as MapFeatureLinkCaptureRegion;
  for (const key of REGION_BOUNDS) {
    const value = regionNumber(source[key]);
    if (value === null) return null;
    out[key] = value;
  }
  for (const key of REGION_CAMERA) {
    const raw = source[key];
    if (raw === null || raw === undefined) { out[key] = null; continue; }
    const value = regionNumber(raw);
    if (value === null) return null;
    out[key] = value;
  }
  const latOk = (v: number) => v >= -90 && v <= 90;
  const lngOk = (v: number) => v >= -180 && v <= 180;
  const pxOk = (v: number) => Number.isInteger(v) && v > 0;
  if (!latOk(out.north) || !latOk(out.south) || !lngOk(out.east) || !lngOk(out.west) || !pxOk(out.width_px) || !pxOk(out.height_px)) {
    return null;
  }
  return { north: out.north, south: out.south, east: out.east, west: out.west, zoom: out.zoom, bearing: out.bearing,
    width_px: out.width_px, height_px: out.height_px };
}

/** What `captureMapView` reads off the ArcGIS view, the extent already in WGS84 (design-context-capture.ts). */
export interface CaptureViewState {
  xmin: number; ymin: number; xmax: number; ymax: number;
  zoom: number;
  /** ArcGIS `view.rotation`: north turned clockwise from the view's top, in degrees. */
  rotation: number;
  /** The screenshot's pixel size. */
  width: number; height: number;
}

/** The region a screenshot pictures (spec addendum §11); the bearing is the compass heading the picture's top faces. */
export function captureRegionFromView(view: CaptureViewState): MapFeatureLinkCaptureRegion | null {
  const rotation = Number(view.rotation);
  const bearing = Number.isFinite(rotation) ? ((360 - rotation) % 360 + 360) % 360 : null;
  const zoom = Number.isFinite(view.zoom) ? view.zoom : null;
  return cleanCaptureRegion({
    north: view.ymax, south: view.ymin, east: view.xmax, west: view.xmin, zoom, bearing,
    width_px: Math.round(view.width), height_px: Math.round(view.height),
  });
}

/** What `AppService.openCarousel` needs to show a capture (the app's image viewer). */
export interface CaptureViewerAttachment {
  original_url: string; preview_url: string; thumb_url: string; mime: 'image/png'; fileType: 'picture'; original_name: string;
}

/**
 * The full picture behind a location slot, for the app's viewer (Sri 2026-09-28, mobile ddfc349d captureViewerUri):
 * a markup just saved on this page, else the marked-up preview when there is markup, else the clean original. No
 * attachment id and no isImage flag, so the viewer never fetches or edits attachment markups for it.
 */
export function captureViewerAttachment(capture: MapFeatureLinkCapture | null | undefined, localPreview?: string | null): CaptureViewerAttachment | null {
  if (!capture || typeof capture !== 'object') return null;
  const url = localPreview || (capture.has_markup ? capture.preview_url : capture.original_url)
    || capture.preview_url || capture.original_url || capture.thumb_url;
  if (!url) return null;
  return { original_url: url, preview_url: url, thumb_url: url, mime: 'image/png', fileType: 'picture', original_name: 'Map capture' };
}

/** Capture scale over the view's CSS size, and the longest side allowed (a large monitor at 2x stays reasonable). */
export const CAPTURE_SCALE = 2;
export const CAPTURE_MAX_SIDE_PX = 4096;

/**
 * The `takeScreenshot` width/height for a picker capture: twice the view's CSS size so the capture stays sharp when
 * it is zoomed in the viewer or the PDF, scaled down (same aspect) if the long side would pass CAPTURE_MAX_SIDE_PX.
 * Null when the view has no size yet.
 */
export function captureScreenshotSize(view: { width: number; height: number }): { width: number; height: number } | null {
  const width = Number(view?.width) * CAPTURE_SCALE;
  const height = Number(view?.height) * CAPTURE_SCALE;
  if (!(width > 0) || !(height > 0)) return null;
  const fit = Math.min(1, CAPTURE_MAX_SIDE_PX / Math.max(width, height));
  return { width: Math.round(width * fit), height: Math.round(height * fit) };
}

/**
 * The one test for "this link has a capture" (spec addendum §11): the capture's image, or null for no capture, a bare
 * uuid string or a capture with no image. The card collapses to one line and the location slot shows the capture
 * instead of the minimap exactly when this is non-null.
 */
export function linkCaptureImage(link: MapFeatureLink | null | undefined): string | null {
  const capture: unknown = link?.capture;
  if (!capture || typeof capture !== 'object') return null;
  const { thumb_url: thumb, preview_url: preview } = capture as MapFeatureLinkCapture;
  return thumb || preview || null;
}

/** The collapsed card's one line: "<label> · <layer> · <offset>", or "On C-112 Rev B · STA 533+50" for a sheet link. */
export function designContextLineText(link: MapFeatureLink): string {
  if (link.sheet) {
    const lines = designContextStripLines(link);
    return [lines.title, lines.detail].filter(Boolean).join(' · ');
  }
  return [designContextChipText(link), offsetText(link)].filter(Boolean).join(' · ');
}

function captureUuid(capture: MapFeatureLinkCapture | string | null): string | null {
  if (typeof capture === 'string') return capture;
  return capture ? capture.attachment_uuid : null;
}

/** A pin coordinate is present: not null/undefined and not blank. */
function hasCoordinate(value: unknown): boolean {
  return value !== null && value !== undefined && `${value}`.trim() !== '';
}

/**
 * Record → api key, with "a pinless record never keeps a map feature" (amendment 1): undefined omits (untouched),
 * null is `mapFeatureLinkPayload`'s (bare, or `{ sheet: null }` when `removed`), otherwise `mapFeatureLinkPayload`.
 * A record without latitude/longitude sends a bare null for a feature link; its sheet target survives: a saved
 * sheet + feature link sends a bare null (R24: the feature part is dropped in place, uuid and tagged redlines kept), a
 * sheet target picked this session (or a draft) is sent alone, and a stored sheet-only link is left untouched.
 * Idempotent on an already-converted payload.
 */
export function recordLinkPayload(
  value: MapFeatureLink | null | undefined, latitude: unknown, longitude: unknown, removed = false,
): MapFeatureLinkPayload | null | undefined {
  if (value === undefined || value === null) return mapFeatureLinkPayload(value, removed);
  if (hasCoordinate(latitude) && hasCoordinate(longitude)) return mapFeatureLinkPayload(value);
  const sheet = sheetTarget(value.sheet);
  if (!sheet) return value.map_feature_ref ? null : mapFeatureLinkPayload(value);
  if (value.map_feature_ref && value.uuid) return null;
  if (!sheetEdited(value)) return undefined;
  return typeof value.client_key === 'string' && value.client_key ? { sheet, snapshot: true, client_key: value.client_key } : { sheet, snapshot: true };
}

/** Report payload keys whose lines carry a pin and so a design context (api `LINKABLE_LINE_ITEMS`). */
export const LINKABLE_REPORT_KEYS = [
  'bid_items', 'material_deliveries', 'activities', 'internal_tests', 'control_forms', 'activity_codes',
] as const;

interface LinkRecord { map_feature_link?: unknown; latitude?: unknown; longitude?: unknown }

/**
 * Whole-report save: every linkable line (including untouched lines re-sent from the fetched
 * report, which hold the serialized link) sends the api payload shape. The serialized shape's
 * `snapshot` object fails the api schema and would reject the whole report. Returns a copy.
 */
export function withLinkPayloads<T>(report: T): T {
  if (!report || typeof report !== 'object') return report;
  const out: Record<string, unknown> = { ...(report as Record<string, unknown>) };
  for (const key of LINKABLE_REPORT_KEYS) {
    const items = out[key];
    if (!Array.isArray(items)) continue;
    out[key] = items.map((item: LinkRecord) => {
      if (!item || typeof item !== 'object' || !('map_feature_link' in item)) return item;
      const { map_feature_link: link, ...rest } = item;
      const payload = recordLinkPayload(link as MapFeatureLink | null | undefined, item.latitude, item.longitude);
      return payload === undefined ? rest : { ...rest, map_feature_link: payload };
    });
  }
  return out as T;
}

/** Copy report: a copied line is a new capture, so no line keeps the source's link (key removed). */
export function stripMapFeatureLinks<T>(report: T): T {
  if (!report || typeof report !== 'object') return report;
  const out: Record<string, unknown> = { ...(report as Record<string, unknown>) };
  for (const [key, items] of Object.entries(out)) {
    if (!Array.isArray(items)) continue;
    out[key] = items.map((item: LinkRecord) => {
      if (!item || typeof item !== 'object' || !('map_feature_link' in item)) return item;
      const rest = { ...item };
      delete rest.map_feature_link;
      return rest;
    });
  }
  return out as T;
}

/** Card "Clear": the pin, location text and any extra location keys go, and the link with them (an explicit removal). */
export function clearLocationControls(group: AbstractControl | null | undefined, extraKeys: string[] = []): void {
  const link = group?.get('map_feature_link');
  if (link?.value) link.markAsDirty();
  for (const key of ['latitude', 'longitude', 'location', ...extraKeys, 'map_feature_link']) {
    group?.get(key)?.setValue(null);
  }
}

/**
 * Patches a picker / station-dialog result into a form group. A result that carries the link (a new pick, or null
 * after Remove) marks the link control dirty: the user changed it in this session (R54).
 */
export function patchPickedLocation(group: AbstractControl | null | undefined, location: unknown, options?: { emitEvent?: boolean }): void {
  if (!group) return;
  const patch = mergePickedLocation(location);
  group.patchValue(patch, options);
  if ('map_feature_link' in patch) group.get('map_feature_link')?.markAsDirty();
}

/** The link control's explicit Remove (no new link): null, marked as changed in this session (R54). */
export function removeLinkControl(group: AbstractControl | null | undefined): void {
  const link = group?.get('map_feature_link');
  if (!link) return;
  link.setValue(null);
  link.markAsDirty();
}

/**
 * A form rebuilt from the old one's values (add a line, add a material): `patchValue` doesn't carry dirtiness, so a link
 * removed in this session would read as untouched afterwards and go out as a bare null, bringing its sheet target back.
 * Rebuilds through this keep the removal: every link control that was a dirty null is marked dirty again (R54).
 */
export function rebuildKeepingLinkRemovals<T extends AbstractControl>(old: AbstractControl | null | undefined, build: () => T): T {
  const values = old?.getRawValue?.();
  const next = build();
  if (values !== undefined) next.patchValue(values);
  carryLinkRemovals(old, next);
  return next;
}

/** Marks dirty each `map_feature_link` in `to` whose control at the same path in `from` is a dirty null */
export function carryLinkRemovals(from: AbstractControl | null | undefined, to: AbstractControl | null | undefined): void {
  if (!from || !to) return;
  const children = (from as AbstractControl & { controls?: Record<string, AbstractControl> | AbstractControl[] }).controls;
  if (!children) return;
  const entries: [string | number, AbstractControl][] = Array.isArray(children) ? children.map((c, i) => [i, c]) : Object.entries(children);
  for (const [key, child] of entries) {
    const target = to.get([key]);
    if (key === 'map_feature_link') {
      if (child.dirty && child.value === null && target?.value === null) target.markAsDirty();
    } else {
      carryLinkRemovals(child, target);
    }
  }
}

/**
 * `getRawValue()` for a save, where a link removed in this session (its control null and dirty) is carried as the
 * removal payload `{ sheet: null }`; an untouched null stays null (R54). Every nested group and array is walked.
 */
export function linkRawValue<T = ReturnType<AbstractControl['getRawValue']>>(control: AbstractControl | null | undefined): T {
  if (!control) return undefined as T;
  const walk = (c: AbstractControl): unknown => {
    const children = (c as AbstractControl & { controls?: Record<string, AbstractControl> | AbstractControl[] }).controls;
    if (!children) return (c as AbstractControl & { getRawValue?: () => unknown }).getRawValue?.() ?? c.value;
    if (Array.isArray(children)) return children.map(walk);
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(children)) {
      out[key] = key === 'map_feature_link' && child.value === null && child.dirty ? { sheet: null } : walk(child);
    }
    return out;
  };
  return walk(control) as T;
}

/**
 * The link a card should show right now. While the record has a `map_feature_link` control its
 * value is the truth, including null after "Remove link"; the saved link is only a fallback for
 * records with no control yet.
 */
/**
 * W51: the strokes held for an unsaved sheet link (a strokes-only PNG of the page), while the link still points at the
 * revision they were drawn on; null otherwise (another sheet picked, nothing held).
 */
export function heldStrokesFor(
  held: { revisionUuid: string; previewDataUrl: string } | null | undefined,
  link: MapFeatureLink | null | undefined,
): string | null {
  return held?.previewDataUrl && held.revisionUuid === link?.sheet?.revision_uuid ? held.previewDataUrl : null;
}

/**
 * W50: whether a record has a location to show (its location card): a station range, a typed location, GPS, or a
 * design link. A sheet-only drawing link has no coordinates, station or text, and is still the record's location.
 */
export function hasRecordLocation(parts: {
  stationRange?: unknown;
  locationText?: unknown;
  hasGps?: boolean;
  link?: MapFeatureLink | null;
}): boolean {
  return !!(parts.stationRange || parts.locationText || parts.hasGps || parts.link);
}

export function currentMapFeatureLink(
  group: AbstractControl | null | undefined,
  savedLink: MapFeatureLink | null | undefined,
): MapFeatureLink | null {
  const control = group?.get('map_feature_link');
  return control ? (control.value ?? null) : (savedLink ?? null);
}

/**
 * Shape a picker/station-dialog result for `FormGroup.patchValue`: each key is patched only when
 * the dialog sent it. Superset of the editors' private `normalizeLocationPayload`, plus the link.
 */
export function mergePickedLocation(location: unknown): Partial<PickedLocationPatch> {
  if (!location || typeof location !== 'object') return {};
  const picked = location as PickedLocationResult;
  return {
    ...(picked.latitude !== undefined ? { latitude: picked.latitude } : {}),
    ...(picked.longitude !== undefined ? { longitude: picked.longitude } : {}),
    ...(picked.location !== undefined || picked.locationText !== undefined ? {
      location: picked.location ?? picked.locationText,
    } : {}),
    ...(picked.station_range !== undefined ? { station_range: picked.station_range } : {}),
    ...(picked.map_feature_link !== undefined ? { map_feature_link: picked.map_feature_link } : {}),
  };
}

/** The picker's unsaved link: reference + pin now, labels for the control, offset after save. */
export function draftMapFeatureLink(query: InspectionQuery, candidate: MapFeatureCandidate, pin: MapFeatureLinkPin): MapFeatureLink {
  return {
    uuid: null,
    map_feature_ref: {
      catalogRevision: query.catalogRevision, layerId: candidate.layer.id, sourceVersionId: candidate.layer.sourceVersionId,
      sublayerKey: candidate.layer.sublayerKey, featureId: candidate.featureId,
    },
    pin: { latitude: pin.latitude, longitude: pin.longitude, ...(typeof pin.accuracy_m === 'number' ? { accuracy_m: pin.accuracy_m } : {}) },
    offset_m: null, bearing_deg: null, offset_label: null,
    feature_label: candidate.label || candidate.fields[0]?.value || candidate.featureId,
    layer_name: candidate.layer.name,
    revision_state: 'current', withdrawn: false, snapshot: null,
  };
}

export function designContextChipText(link: MapFeatureLink): string {
  if (link.sheet) return sheetTitle(link.sheet);
  return featureChipText(link);
}

function featureChipText(link: MapFeatureLink): string {
  const label = link.feature_label || link.map_feature_ref?.featureId || 'Feature';
  return link.layer_name ? `${label} · ${link.layer_name}` : label;
}

/** A sheet-only link has no offset; a feature link shows the api's label, or the draft copy. */
export function offsetText(link: MapFeatureLink): string {
  if (link.sheet && !link.map_feature_ref) return '';
  return link.offset_label ? `${link.offset_label} of design` : 'Offset calculated on save';
}

/** "On C-112 Rev B" (spec §12 slice 3). */
function sheetTitle(sheet: MapFeatureLinkSheet): string {
  return `On ${[sheet.sheet_number || 'sheet', sheet.label ? `Rev ${sheet.label}` : ''].filter(Boolean).join(' ')}`;
}

/** A station in feet as `533+50`, as the api's PDF line prints it. */
function stationText(value: number): string {
  const feet = Math.round(Number(value));
  return `${Math.floor(feet / 100)}+${String(feet % 100).padStart(2, '0')}`;
}

/** The sheet's station range like the api PDF line (`sheetLabelText`): "STA 533+50", or "STA 530+00 to 540+00". */
export function sheetStationText(start: number | null | undefined, end: number | null | undefined): string {
  const hasStart = start !== null && start !== undefined && Number.isFinite(Number(start));
  const hasEnd = end !== null && end !== undefined && Number.isFinite(Number(end));
  // An open end runs to the project's begin or end (R39/R42): "STA begin to 1006+00", "STA 1066+00 to end"
  if (hasStart && hasEnd) {
    return Number(end) === Number(start) ? `STA ${stationText(start)}` : `STA ${stationText(start)} to ${stationText(end)}`;
  }
  if (hasStart) return `STA ${stationText(start)} to end`;
  return hasEnd ? `STA begin to ${stationText(end)}` : '';
}

/** A station input's text: feet as `533+50`, '' when unset. */
export function stationInputText(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(Number(value)) ? '' : stationText(value);
}

/** A typed station ("533+50" or feet) in feet: null when blank (clears it), undefined when it is not a station. */
export function parseStationInput(text: string | null | undefined): number | null | undefined {
  if (!text?.trim()) return null;
  return stationFeet(text.trim()) ?? undefined;
}

/** A record's station ("533+50", "53350" or a number of feet) in feet; null when it is not one. */
export function stationFeet(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const plus = /^\s*(\d+)\+(\d+(?:\.\d+)?)\s*$/.exec(value);
  if (plus) return Number(plus[1]) * 100 + Number(plus[2]);
  const feet = Number(value);
  return Number.isFinite(feet) ? feet : null;
}

/**
 * The picker's unsaved sheet link (Drawing tab): the revision and the clicked point (null for markup with no pin), its page the revision's
 * `page_number` (api 422 DRAWING_SHEET_POINT_PAGE otherwise). On the link's own revision a moved point keeps the
 * link's uuid (the api updates it in place); a feature link keeps its feature, so the payload re-sends the reference
 * and pin (amendment 2). Any other base becomes a new link, and a map capture does not follow it.
 */
export function draftSheetLink(
  revision: DrawingRevisionDetail, sheet: Pick<DrawingSheet, 'uuid' | 'number' | 'station_start' | 'station_end'> | null,
  point: { x: number; y: number } | null, base: MapFeatureLink | null,
): MapFeatureLink {
  const clamp = (v: number) => Math.min(1, Math.max(0, Number(v) || 0));
  const sameRevision = !!base?.sheet && base.sheet.revision_uuid === revision.uuid;
  const feature = base?.map_feature_ref ? base : null;
  return {
    uuid: sameRevision ? base.uuid : null,
    map_feature_ref: feature?.map_feature_ref ?? null,
    pin: feature?.pin ?? null,
    sheet: {
      revision_uuid: revision.uuid,
      sheet_uuid: revision.sheet?.uuid ?? sheet?.uuid ?? null,
      sheet_number: revision.sheet?.number ?? sheet?.number ?? null,
      label: revision.label ?? null,
      point: point ? { page: revision.page_number, x: clamp(point.x), y: clamp(point.y) } : null,
      station_start: sheet?.station_start ?? null,
      station_end: sheet?.station_end ?? null,
      status: revision.status ?? null,
      superseded: revision.status === 'superseded',
      withdrawn: revision.status === 'withdrawn',
      marker_index: sameRevision ? base.sheet.marker_index : null,
    },
    offset_m: feature?.offset_m ?? null, bearing_deg: feature?.bearing_deg ?? null, offset_label: feature?.offset_label ?? null,
    feature_label: feature?.feature_label ?? null, layer_name: feature?.layer_name ?? null,
    revision_state: feature?.revision_state ?? 'current', withdrawn: feature?.withdrawn ?? false,
    // The api re-renders the crop on save: a saved link's old crop shows the old point.
    snapshot: sameRevision && base.uuid
      ? { status: 'pending', error: null, attachment_uuid: null, thumb_url: null, preview_url: null, has_markup: false } : null,
    ...(sameRevision && base.capture !== undefined ? { capture: base.capture } : {}),
    // Picked in this session: the payload sends this sheet target (R54)
    sheet_edited: true,
  };
}

/**
 * Spec §7.2 badge; null when the link still points at the map's current revision and its layer
 * is live. "Layer withdrawn" only when the api says so; an unknown current revision (loading, offline) gets no suffix.
 */
/**
 * The picker strip's two text lines (spec addendum §10, mobile's `designContextStripLines`): the feature label on its
 * own line, so nothing squeezes it away, then layer · offset. Without a label the layer (or "Design feature") leads.
 */
export function designContextStripLines(link: MapFeatureLink): { title: string; detail: string } {
  if (link.sheet) {
    const station = sheetStationText(link.sheet.station_start, link.sheet.station_end);
    return { title: sheetTitle(link.sheet), detail: [station, link.map_feature_ref ? featureChipText(link) : ''].filter(Boolean).join(' · ') };
  }
  const label = link.feature_label || link.map_feature_ref?.featureId || '';
  const layer = link.layer_name || '';
  return { title: label || layer || 'Design feature', detail: [label ? layer : '', offsetText(link)].filter(Boolean).join(' · ') };
}

export function revisionBadgeText(link: MapFeatureLink, currentRevision: number | null): string | null {
  // A sheet link (spec §2): it keeps opening the revision it was made on, flagged once that is no longer current.
  if (link.sheet?.withdrawn) return 'Withdrawn';
  if (link.sheet?.superseded) return 'Superseded';
  if (!link.map_feature_ref) return null;
  // Mobile's copy: a withdrawn layer says so on its own, whatever the revision state.
  if (link.withdrawn) return 'Layer withdrawn';
  if (link.revision_state !== 'superseded') return null;
  const linked = `Linked to rev ${link.map_feature_ref.catalogRevision}`;
  return currentRevision === null ? linked : `${linked} · map now rev ${currentRevision}`;
}

/** ~0.1 m: the api stores attachment coordinates as varchar, so a round trip may add float noise. */
export const CAPTURE_PIN_TOLERANCE_DEG = 1e-6;

/**
 * "Retake?" (spec 2026-09-27 §3.4): the capture pictured another pin. Longitude is compared
 * sign-insensitively because `AttachmentService.add` stores it as `-(Math.abs(longitude))`.
 */
export function captureNeedsRetake(link: MapFeatureLink | null | undefined): boolean {
  const capture = link?.capture;
  const pin = link?.pin;
  if (!capture || !pin) return false;
  // The api returns varchar coordinates; a missing or blank value, or anything that isn't a finite
  // number, can't prove a move (null must never become Number(null) === 0).
  const coord = (v: unknown) => (v == null || (typeof v === 'string' && !v.trim()) ? NaN : Number(v));
  const [capLat, capLon] = [coord(capture.latitude), coord(capture.longitude)];
  if (!Number.isFinite(capLat) || !Number.isFinite(capLon)) return false;
  const lat = Math.abs(capLat - Number(pin.latitude));
  const lon = Math.abs(Math.abs(capLon) - Math.abs(Number(pin.longitude)));
  return !(lat < CAPTURE_PIN_TOLERANCE_DEG && lon < CAPTURE_PIN_TOLERANCE_DEG);
}

export function withCapture(link: MapFeatureLink, capture: MapFeatureLinkCapture | null): MapFeatureLink {
  return { ...link, capture };
}

/** The capture after the markup editor saved: strokes → marked-up images; a cleared save → the clean image again. */
export function captureAfterMarkupSave(capture: MapFeatureLinkCapture, result: MarkupSaveResult): MapFeatureLinkCapture {
  const hasMarkup = !!result.markup?.annotation_data?.objects?.length;
  return {
    ...capture,
    has_markup: hasMarkup,
    thumb_url: result.updatedThumbUrl || result.previewDataUrl || capture.thumb_url,
    preview_url: hasMarkup
      ? result.markedUpUrl || result.updatedPreviewUrl || capture.preview_url
      : capture.original_url || result.updatedPreviewUrl || capture.preview_url,
  };
}

/** Map marker filter (spec §7.3): the record itself or any grouped line item carries a link. */
export function markerIsLinked(marker: unknown): boolean {
  if (!marker || typeof marker !== 'object') return false;
  const m = marker as LinkMarker;
  if (m.map_feature_link || m.data?.map_feature_link) return true;
  return (m.data?.allLineItems || []).some(item => !!item?.map_feature_link);
}

export function hasMapFeatureLink(values: unknown): boolean {
  const link = (values as LinkCarrier | null | undefined)?.map_feature_link;
  return !!link && typeof link === 'object';
}

/** Report cards that group several records (bid-item lines, test stations, delivery materials): how many carry a link. */
export function linkedRecordCount(records: readonly unknown[] | null | undefined): number {
  return (records || []).filter(record => hasMapFeatureLink(record)).length;
}

export function linkedCountText(count: number): string {
  return `${count} linked to design`;
}

/** Toolbar contexts whose record has no `map_feature_link` control (see `toolbarPickHasDesignContext`). */
const TOOLBAR_CONTEXTS_WITHOUT_LINK = ['usage', 'external_test'];

/**
 * Whether a daily-report toolbar pin pick may offer design context. The toolbar can't see the
 * record's form, so it decides from the section and the host's toolbar context: item postings
 * (spec §1), material usage (shares the `delivery-usage` section with deliveries) and external
 * tests (create-external-test keeps only lat/lng) have no `map_feature_link` control, so a link
 * picked there would be dropped. The multiline bid-item editor (`bid_items`) opens under
 * `item-postings` but has a control.
 */
export function toolbarPickHasDesignContext(expandedSection: string | null | undefined, toolbarContext: string): boolean {
  if (TOOLBAR_CONTEXTS_WITHOUT_LINK.includes(toolbarContext)) return false;
  return expandedSection !== 'item-postings' || toolbarContext === 'bid_items';
}

/** Daily-report sections (`expandedSection`) whose shared toolbar opens the picker, as the record the picker links. */
const TOOLBAR_RECORD_LABELS: Record<string, string> = {
  'issues': 'issue',
  'activities': 'activity',
  'activity-codes': 'activity code',
  'delivery-usage': 'delivery',
  'material-testing': 'test',
  'testing-forms': 'test',
  'forms': 'form',
  'control-forms': 'form',
  'bid-item': 'bid item',
  'item-postings': 'bid item',
};

/** "Link to this <record>" (spec §7.1) for a toolbar pick: the expanded section names the record; unknown → "record". */
export function toolbarRecordLabel(expandedSection: string | null | undefined, toolbarContext: string): string {
  if (toolbarContext === 'bid_items') return 'bid item';
  return TOOLBAR_RECORD_LABELS[expandedSection || ''] || 'record';
}

/** Type-filter token (spec §7.3 map filter): present = draw only records that carry a design context link. */
export const LINKED_ONLY_FILTER = 'linked_only';

/**
 * Spec §10: the Types tab's linked-only filter exists only for a project with a published design
 * catalog. Hidden until the answer arrives; a "no" (404, error, no project) also drops a ticked
 * token, so markers are never filtered by a control the user can no longer see.
 */
export class LinkedOnlyFilterGate {
  visible = false;
  private projectId: string | null = null;

  constructor(private readonly isAvailable: (projectId: string) => Promise<boolean>) {}

  /**
   * Checks `projectId` (a repeat of the current one is a no-op). Resolves to the type filters to
   * emit when the token had to be dropped, else null. An answer for a superseded project is ignored.
   */
  async check(projectId: string, currentFilters: () => string[]): Promise<string[] | null> {
    const id = projectId || null;
    if (id === this.projectId && this.projectId !== null) return null;
    this.projectId = id;
    this.visible = false;
    let available = false;
    if (id) {
      try { available = await this.isAvailable(id); } catch { available = false; }
    }
    if (this.projectId !== id) return null;
    this.visible = !!available;
    if (this.visible) return null;
    const filters = currentFilters() || [];
    return filters.includes(LINKED_ONLY_FILTER) ? filters.filter(filter => filter !== LINKED_ONLY_FILTER) : null;
  }
}

/** "Captured here" (spec §7.3): rows requested per feature; the panel then says "n more". */
export const CAPTURED_HERE_ROWS = 6;
/** The api counts at most this many linked records per feature (`meta.totalRecords` caps here). */
export const CAPTURED_HERE_CAP = 200;

/** One label per api `map_feature_link.entity_type` (the record the link belongs to). */
const CAPTURED_HERE_TYPE_LABELS: Record<string, string> = {
  issue: 'Issue',
  report_bid_item: 'Bid item',
  report_material_delivery: 'Delivery',
  report_activity: 'Activity',
  report_internal_test: 'Test',
  report_form_header: 'Form',
  report_activity_code_header: 'Activity codes',
  daily_diary_remark: 'Diary remark',
  punchlist_item: 'Punch item',
  rfi: 'RFI',
  warranty_claim: 'Warranty claim',
};

export function capturedHereTypeLabel(entityType: string): string {
  return CAPTURED_HERE_TYPE_LABELS[entityType] || 'Record';
}

/** The section heading count: "200+" once the api's cap is reached. */
export function capturedHereCountText(total: number): string {
  return total >= CAPTURED_HERE_CAP ? `${CAPTURED_HERE_CAP}+` : `${Math.max(0, total || 0)}`;
}

/** The line under the rows: "" when every record is listed; a capped total keeps its "+". */
export function capturedHereMoreText(total: number, shown: number): string {
  const more = Math.max(0, Math.min(total, CAPTURED_HERE_CAP) - shown);
  if (!more) return '';
  return total >= CAPTURED_HERE_CAP ? `${more}+ more` : `${more} more`;
}

/** A Captured-here row as the panel shows it: title, type and date, then author · offset · photos. */
export interface CapturedHereItem {
  id: string;
  title: string;
  type: string;
  date: Date | null;
  detail: string;
}

/** 'YYYY-MM-DD' is a calendar day: build it in local time (a Date parse would read it as UTC midnight). */
function capturedHereDate(row: CapturedHereRow): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(row.report_date || '');
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  const created = row.created_at ? new Date(row.created_at) : null;
  return created && !Number.isNaN(created.getTime()) ? created : null;
}

export function capturedHereItem(row: CapturedHereRow): CapturedHereItem {
  const type = capturedHereTypeLabel(row.entity_type);
  const author = row.created_by;
  const who = author ? `${author.first_name || ''} ${author.last_name ? `${author.last_name[0]}.` : ''}`.trim() : '';
  const photos = row.photo_count > 0 ? `${row.photo_count} ${row.photo_count === 1 ? 'photo' : 'photos'}` : '';
  return {
    id: row.id,
    title: row.entity_label?.trim() || type,
    type,
    date: capturedHereDate(row),
    detail: [who, row.offset_label, photos].filter(Boolean).join(' · '),
  };
}

/** 7.2: the link's markup kind and review status after a save on its row (an as-built mark saves open) */
export function savedMarkupKind(markup: Markup | null | undefined, hasStrokes: boolean):
  { markup_kind: 'as_built' | 'sketch' | null; review_status: 'open' | 'reviewed' | 'rejected' | 'incorporated' | null } {
  if (!hasStrokes) return { markup_kind: null, review_status: null };
  return markup?.kind === 'as_built'
    ? { markup_kind: 'as_built', review_status: markup.review_status ?? 'open' } : { markup_kind: 'sketch', review_status: null };
}

/** 7.2: an issue whose category reads as-built (As-built, As Built, as-built…) marks up as As-built by default */
export function recordMarkupKind(category: string | null | undefined): 'note' | 'as_built' {
  return /as[\s-]?built/i.test(category || '') ? 'as_built' : 'note';
}
